import { AssetLoader } from '../../asset/AssetLoader';
import { NoticeStack } from './NoticeStack';
import { OverlayStage } from './OverlayStage';
import { RefillCelebration, type RefillSnapshot } from './RefillCelebration';

/** The art the refill needs, by URL (the app's bundled files). */
export interface RefillArtUrls {
  coin: string;
  chest: string;
}

declare global {
  interface Window {
    /** The refill moment on screen, for browser tests. */
    __refill?: { ui: () => RefillSnapshot };
  }
}

const COIN = 'shared:icon:coin';
const CHEST = 'shared:reel:coin_chest';

let queue: Promise<void> = Promise.resolve();

/**
 * Play the free refill over whatever is on screen, on the overlay canvas.
 * Refills arriving while one plays wait their turn. Resolves when it has
 * gone.
 */
export function playRefill(options: {
  art: RefillArtUrls;
  from: number;
  amount: number;
  /** Where the coin counter is (screen pixels), or null. */
  target: () => { x: number; y: number } | null;
}): Promise<void> {
  queue = queue.then(async () => {
    const loader = AssetLoader.shared;
    await loader
      .loadTextures([
        { alias: COIN, src: options.art.coin },
        { alias: CHEST, src: options.art.chest },
      ])
      .catch(() => undefined);
    const piece = new RefillCelebration((name) => loader.getTexture(name === 'coin' ? COIN : CHEST));
    const handle = OverlayStage.shared.show(piece, { layer: 20 });
    window.__refill = { ui: () => piece.snapshot };
    // The whole screen is the refill's: notices wait until it's over.
    const unblock = NoticeStack.shared.block();
    try {
      if (await handle.shown) await piece.play(options);
    } finally {
      unblock();
      handle.remove();
      piece.destroy({ children: true });
      delete window.__refill;
    }
  });
  return queue;
}
